import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, CheckSquare, XCircle, Clock, DollarSign, Trash2, Filter, Ban, AlertTriangle } from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { RecordLink } from '@/components/records/RecordLink';
import { useRowOpen } from '@/components/records/useRowOpen';
import { getDefaultBankAccountId, type BankAccountOption } from '@/lib/bank-accounts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '@/lib/utils';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { AlertBanner } from '@/components/ui/alert-banner';
import { StatusBadge } from '@/components/ui/status-badge';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

type ChequeType = 'RECEIVABLE' | 'PAYABLE';
type ChequeStatus = 'PENDING' | 'DEPOSITED' | 'CLEARED' | 'BOUNCED' | 'CANCELLED';
type ChequeSource = 'SALE' | 'MANUAL' | 'KHATA_PAYMENT';

/** Legacy manual receivables (T-492): typed in by hand before receivables moved to POS / Khata. */
const LEGACY_QUERY = 'type=RECEIVABLE&source=MANUAL&status=PENDING,DEPOSITED';

interface Cheque {
  id: string;
  chequeNo: string;
  type: ChequeType;
  /** Origin marker: POS tender (SALE), Khata cheque payment, or typed in Finance > Cheques (MANUAL). */
  source?: ChequeSource;
  status: ChequeStatus;
  amount: number;
  drawerName: string;
  bankName: string;
  chequeDate: string;
  dueDate: string | null;
  notes: string | null;
  customer?: { name: string } | null;
  supplier?: { name: string } | null;
  /** Originating POS sale for a CHEQUE tender (T-480). */
  sale?: { id: string; invoiceNumber: string } | null;
  /** Deposit / clearing bank account (T-480). */
  bankAccount?: { id: string; name: string } | null;
}

/** POST /cheques request body. */
interface CreateChequePayload {
  chequeNo: string;
  /** Manual cheques are payables only (T-492). */
  type: 'PAYABLE';
  amount: number;
  drawerName: string;
  bankName: string;
  chequeDate: string;
  dueDate?: string;
  notes?: string;
}

const formatDate = (d: string) => new Date(d).toLocaleDateString('en-PK');

export function ChequesPage() {
  const rowOpen = useRowOpen();
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('finance');
  const canWrite = hasPermission('cheques:write') || hasPermission('cheques:*');
  const canDelete = hasPermission('cheques:delete') || hasPermission('cheques:*');

  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<ChequeType | ''>('');
  const [statusFilter, setStatusFilter] = useState<ChequeStatus | ''>('');
  const [legacyOnly, setLegacyOnly] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState<{ cheque: Cheque; newStatus: ChequeStatus } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [depositBankId, setDepositBankId] = useState('');
  const [form, setForm] = useState({
    chequeNo: '', amount: '',
    drawerName: '', bankName: '', chequeDate: '', dueDate: '', notes: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['cheques', typeFilter, statusFilter, legacyOnly],
    queryFn: () => {
      if (legacyOnly) return api.get(`/cheques?${LEGACY_QUERY}&limit=100`).then((r) => r.data);
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      if (statusFilter) params.set('status', statusFilter);
      return api.get(`/cheques?${params}`).then((r) => r.data);
    },
  });

  // Legacy manual receivables still open for this tenant (T-492) — drives the banner.
  const { data: legacyData } = useQuery<{ total: number }>({
    queryKey: ['cheques', 'legacy-count'],
    queryFn: () => api.get(`/cheques?${LEGACY_QUERY}&limit=1`).then((r) => r.data as { total: number }),
    staleTime: 30_000,
  });
  const legacyCount = legacyData?.total ?? 0;

  const cheques: Cheque[] = data?.data ?? [];

  // Receivable deposit / clear (T-480) credits a bank account; a payable clear (T-485) debits the
  // account it is paid from — ask which one when there is a choice.
  const needsBank = !!statusDialog
    && ((statusDialog.cheque.type === 'RECEIVABLE' && (statusDialog.newStatus === 'DEPOSITED' || statusDialog.newStatus === 'CLEARED'))
      || (statusDialog.cheque.type === 'PAYABLE' && statusDialog.newStatus === 'CLEARED'));
  const { data: bankAccounts = [] } = useQuery<BankAccountOption[]>({
    queryKey: ['bank-accounts-options'],
    queryFn: () => api.get('/bank-accounts/options').then((r) => r.data as BankAccountOption[]),
    enabled: needsBank,
    staleTime: 60_000,
  });
  const effectiveBankId = depositBankId || statusDialog?.cheque.bankAccount?.id || getDefaultBankAccountId(bankAccounts);

  const addMutation = useMutation({
    mutationFn: (d: CreateChequePayload) => api.post('/cheques', d).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('chequeAdded'));
      queryClient.invalidateQueries({ queryKey: ['cheques'] });
      setAddOpen(false);
      setForm({ chequeNo: '', amount: '', drawerName: '', bankName: '', chequeDate: '', dueDate: '', notes: '' });
    },
    onError: (err) => notify.error(err, t('failedToAddCheque')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, bankAccountId }: { id: string; status: string; bankAccountId?: string }) =>
      api.patch(`/cheques/${id}/status`, { status, bankAccountId }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('statusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['cheques'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts-options'] });
      queryClient.invalidateQueries({ queryKey: ['khata-outstanding'] });
      setStatusDialog(null);
      setDepositBankId('');
    },
    onError: (err) => notify.error(err, t('failedToUpdateStatus')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/cheques/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('chequeDeleted'));
      queryClient.invalidateQueries({ queryKey: ['cheques'] });
      setDeleteId(null);
    },
    onError: (err) => notify.error(err, t('failedToDelete')),
  });

  const handleAdd = () => {
    if (!form.chequeNo || !form.amount || !form.drawerName || !form.bankName || !form.chequeDate) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    if (parseFloat(form.amount) <= 0) { toast.error(t('amountMustBePositive')); return; }
    addMutation.mutate({
      chequeNo: form.chequeNo,
      type: 'PAYABLE',
      amount: parseFloat(form.amount),
      drawerName: form.drawerName,
      bankName: form.bankName,
      chequeDate: form.chequeDate,
      dueDate: form.dueDate || undefined,
      notes: form.notes || undefined,
    });
  };

  const totalPending = cheques
    .filter((c) => c.status === 'PENDING')
    .reduce((s, c) => s + c.amount, 0);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('chequeManagement')}
        subtitle={t('trackCheques')}
        actions={canWrite ? (
          <Button onClick={() => setAddOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" /> {t('addCheque')}
          </Button>
        ) : undefined}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {(['PENDING', 'DEPOSITED', 'CLEARED', 'BOUNCED'] as ChequeStatus[]).map((s) => {
          const count = cheques.filter((c) => c.status === s).length;
          const statusLabel: Record<ChequeStatus, string> = {
            PENDING: t('statusPending'),
            DEPOSITED: t('statusDeposited'),
            CLEARED: t('statusCleared'),
            BOUNCED: t('statusBounced'),
            CANCELLED: t('statusCancelled'),
          };
          return (
            <Card key={s} className={legacyOnly ? undefined : 'cursor-pointer hover:shadow-md transition-shadow'} onClick={legacyOnly ? undefined : () => setStatusFilter(s === statusFilter ? '' : s)}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">{statusLabel[s]}</p>
                <p className="text-2xl font-bold">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {totalPending > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-warning-text shrink-0" />
          <p className="text-sm text-warning-text">
            <strong>{formatCurrency(totalPending)}</strong> {t('totalPendingCheques', { count: cheques.filter((c) => c.status === 'PENDING').length })}
          </p>
        </div>
      )}

      {legacyCount > 0 && (
        <AlertBanner tone="warning" icon={AlertTriangle} className="mb-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">{t('legacyManualReceivables')} ({legacyCount})</p>
              <p className="text-xs">{t('legacyManualReceivablesHelp')}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setLegacyOnly((v) => !v)}>
              {legacyOnly ? t('showAllCheques') : t('showLegacyReceivables')}
            </Button>
          </div>
        </AlertBanner>
      )}

      {/* Filters */}
      {legacyOnly ? (
        <p className="text-xs text-muted-foreground">{t('legacyFiltersIgnored')}</p>
      ) : (
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Dropdown
            className="text-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.value as ChequeType | '')}
            options={[
              { label: t('allTypes'), value: '' },
              { label: t('typeReceivable'), value: 'RECEIVABLE' },
              { label: t('typePayable'), value: 'PAYABLE' },
            ]}
            optionLabel="label"
            optionValue="value"
            placeholder={t('allTypes')}
          />
        </div>
        <Dropdown
          className="text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.value as ChequeStatus | '')}
          options={[
            { label: t('allStatuses'), value: '' },
            ...(['PENDING', 'DEPOSITED', 'CLEARED', 'BOUNCED', 'CANCELLED'] as ChequeStatus[]).map((s) => {
              const statusLabel: Record<ChequeStatus, string> = {
                PENDING: t('statusPending'),
                DEPOSITED: t('statusDeposited'),
                CLEARED: t('statusCleared'),
                BOUNCED: t('statusBounced'),
                CANCELLED: t('statusCancelled'),
              };
              return { label: statusLabel[s], value: s };
            }),
          ]}
          optionLabel="label"
          optionValue="value"
          placeholder={t('allStatuses')}
        />
      </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <SkeletonTable rows={5} cols={8} />
          ) : cheques.length === 0 ? (
            <EmptyState
              icon={CheckSquare}
              title={t('noCheques')}
              subtitle={canWrite ? t('addFirstCheque') : undefined}
              action={canWrite ? { label: t('addCheque'), onClick: () => setAddOpen(true) } : undefined}
            />
          ) : (
            <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('chequeNo')}</Th>
                    <Th>{t('type')}</Th>
                    <Th>{t('drawerParty')}</Th>
                    <Th>{t('bankName')}</Th>
                    <Th>{t('chequeDate')}</Th>
                    <Th>{t('dueDate')}</Th>
                    <Th className="text-end">{t('amount')}</Th>
                    <Th>{t('status')}</Th>
                    <Th>{t('actions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {cheques.map((c) => {
                    // Legacy manual receivable: cannot be deposited / cleared (no DR 1100 was ever posted);
                    // it can only be retired (bounce / cancel) after re-entry as a Khata cheque payment.
                    const isLegacy = c.type === 'RECEIVABLE' && c.source === 'MANUAL';
                    return (
                    <Tr key={c.id} {...rowOpen('cheque', c.id, c.chequeNo)}>
                      <Td label={t('chequeNo')} className="font-mono font-medium text-foreground">
                        <RecordLink type="cheque" id={c.id} label={c.chequeNo} variant="mono" />
                        {c.sale && (
                          <span className="block font-sans text-xs font-normal"><RecordLink type="invoice" id={c.sale.id} label={c.sale.invoiceNumber} variant="mono" className="text-xs" /></span>
                        )}
                        {c.bankAccount && (
                          <span className="block font-sans text-xs font-normal text-muted-foreground">{c.bankAccount.name}</span>
                        )}
                        {isLegacy && (
                          <span className="block font-sans text-xs font-normal text-warning-text">{t('legacyManualReceivable')}</span>
                        )}
                      </Td>
                      <Td label={t('type')}>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.type === 'RECEIVABLE' ? 'bg-info/10 text-info-text' : 'bg-primary/10 text-primary-text'}`}>
                          {c.type}
                        </span>
                      </Td>
                      <Td label={t('drawerParty')}>
                        <p className="font-medium text-foreground">{c.drawerName}</p>
                        {(c.customer || c.supplier) && (
                          <p className="text-xs text-muted-foreground">{c.customer?.name ?? c.supplier?.name}</p>
                        )}
                      </Td>
                      <Td label={t('bankName')} className="text-muted-foreground">{c.bankName}</Td>
                      <Td label={t('chequeDate')} className="text-muted-foreground">{formatDate(c.chequeDate)}</Td>
                      <Td label={t('dueDate')} className="text-muted-foreground">
                        {c.dueDate ? (
                          <span className={new Date(c.dueDate) < new Date() && c.status === 'PENDING' ? 'text-destructive-text font-medium' : ''}>
                            {formatDate(c.dueDate)}
                          </span>
                        ) : <span className="text-muted-foreground/50">—</span>}
                      </Td>
                      <Td label={t('amount')} className="text-end font-semibold text-foreground">{formatCurrency(c.amount)}</Td>
                      <Td label={t('status')}>
                        <StatusBadge status={c.status} />
                      </Td>
                      <Td label={t('actions')} className="text-end td-actions" onClick={(e) => e.stopPropagation()}>
                        <TableRowActions
                          actions={[
                            ...(canWrite && isLegacy && (c.status === 'PENDING' || c.status === 'DEPOSITED') ? [
                              {
                                key: 'cancel',
                                label: t('cancelCheque'),
                                icon: Ban,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'CANCELLED' }),
                                primary: true,
                                labeled: true,
                              },
                              {
                                key: 'bounce',
                                label: t('bounce'),
                                icon: XCircle,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'BOUNCED' }),
                                variant: 'destructive' as const,
                                labeled: true,
                              },
                            ] : []),
                            ...(canWrite && !isLegacy && c.status === 'PENDING' ? [
                              {
                                key: 'deposit',
                                label: t('deposit'),
                                icon: Clock,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'DEPOSITED' }),
                                primary: true,
                                labeled: true,
                              },
                              {
                                key: 'clear',
                                label: t('clear'),
                                icon: CheckSquare,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'CLEARED' }),
                                labeled: true,
                              },
                              {
                                key: 'bounce',
                                label: t('bounce'),
                                icon: XCircle,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'BOUNCED' }),
                                variant: 'destructive' as const,
                                labeled: true,
                              },
                            ] : []),
                            ...(canWrite && !isLegacy && c.status === 'DEPOSITED' ? [
                              {
                                key: 'clear',
                                label: t('clear'),
                                icon: CheckSquare,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'CLEARED' }),
                                primary: true,
                                labeled: true,
                              },
                              {
                                key: 'bounce',
                                label: t('bounce'),
                                icon: XCircle,
                                onSelect: () => setStatusDialog({ cheque: c, newStatus: 'BOUNCED' }),
                                variant: 'destructive' as const,
                                labeled: true,
                              },
                            ] : []),
                            {
                              key: 'delete',
                              label: t('delete'),
                              icon: Trash2,
                              onSelect: () => setDeleteId(c.id),
                              variant: 'destructive',
                              // T-496: SALE / KHATA_PAYMENT cheques carry the AR link - cancel or bounce, never delete.
                              permission: canDelete && (c.source ?? 'MANUAL') === 'MANUAL',
                            },
                          ]}
                        />
                      </Td>
                    </Tr>
                    );
                  })}
                </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-scrim/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-semibold">{t('addCheque')}</h2>
            <p className="text-xs text-muted-foreground">{t('manualChequePayableOnly')}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('chequeNo')} *</Label>
                <Input value={form.chequeNo} onChange={(e) => setForm({ ...form, chequeNo: e.target.value })} placeholder={t('chequeNoPlaceholder')} />
              </div>
              <div>
                <Label className="text-xs">{t('amountPkr')} *</Label>
                <Input type="number" min="1" step="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="50000" />
              </div>
              <div>
                <Label className="text-xs">{t('drawerName')} *</Label>
                <Input value={form.drawerName} onChange={(e) => setForm({ ...form, drawerName: e.target.value })} placeholder={t('drawerNamePlaceholder')} />
              </div>
              <div>
                <Label className="text-xs">{t('bankName')} *</Label>
                <Input value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} placeholder={t('chequeBankPlaceholder')} />
              </div>
              <div>
                <Label className="text-xs">{t('chequeDate')} *</Label>
                <Input type="date" value={form.chequeDate} onChange={(e) => setForm({ ...form, chequeDate: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t('dueDate')}</Label>
                <Input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">{t('notes')}</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t('optionalNotes')} />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setAddOpen(false)}>{t('cancel')}</Button>
              <Button onClick={handleAdd} disabled={addMutation.isPending}>
                {addMutation.isPending ? t('adding') : t('addCheque')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Receivable deposit / clear: pick the bank account that receives the cheque */}
      <Dialog
        header={t('markAsStatus', { status: statusDialog?.newStatus ?? '' })}
        visible={needsBank}
        onHide={() => { setStatusDialog(null); setDepositBankId(''); }}
        className="w-full max-w-md"
        draggable={false}
      >
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">
            {t('changeStatusConfirm', { no: statusDialog?.cheque.chequeNo ?? '', status: statusDialog?.newStatus ?? '' })}
            {statusDialog?.newStatus === 'CLEARED' ? ' ' + t('journalEntryWillBePosted') : ''}
          </p>
          {bankAccounts.length > 0 ? (
            <div>
              <Label className="text-xs">
                {statusDialog?.cheque.type === 'PAYABLE' ? t('payFromBankAccount') : t('depositBankAccount')} *
              </Label>
              <Dropdown
                value={effectiveBankId}
                onChange={(e) => setDepositBankId(e.value as string)}
                options={bankAccounts.map((acc) => ({ label: `${acc.name} — ${acc.bankName}`, value: acc.id }))}
                optionLabel="label"
                optionValue="value"
                className="mt-1 w-full"
              />
            </div>
          ) : (
            <p className="text-xs text-warning-text">{t('noBankAccountsForCheque')}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setStatusDialog(null); setDepositBankId(''); }}>{t('cancel')}</Button>
            <Button
              disabled={statusMutation.isPending || !effectiveBankId}
              onClick={() => statusDialog && statusMutation.mutate({ id: statusDialog.cheque.id, status: statusDialog.newStatus, bankAccountId: effectiveBankId })}
            >
              {statusDialog?.newStatus === 'CLEARED' ? t('markCleared') : t('confirm')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Status change confirmation (everything except a receivable deposit / clear) */}
      <ConfirmDialog
        open={!!statusDialog && !needsBank}
        title={t('markAsStatus', { status: statusDialog?.newStatus ?? '' })}
        message={`${t('changeStatusConfirm', { no: statusDialog?.cheque.chequeNo ?? '', status: statusDialog?.newStatus ?? '' })}${statusDialog?.newStatus === 'CLEARED' ? ' ' + t('journalEntryWillBePosted') : ''}`}
        confirmLabel={statusDialog?.newStatus === 'BOUNCED' ? t('markBounced') : statusDialog?.newStatus === 'CLEARED' ? t('markCleared') : t('confirm')}
        variant={statusDialog?.newStatus === 'BOUNCED' ? 'danger' : 'warning'}
        onConfirm={() => statusDialog && statusMutation.mutate({ id: statusDialog.cheque.id, status: statusDialog.newStatus })}
        onCancel={() => setStatusDialog(null)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteId}
        title={t('deleteCheque')}
        message={t('deleteChequeMessage')}
        confirmLabel={t('delete')}
        variant="danger"
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
